/**
 * @file apps/web/src/pages/ProjectMembersPage.tsx
 * @description Who is on this project. Everyone on the project can read the
 * roster; a project admin (or workspace admin/owner) can also invite by email
 * and role, resend or revoke pending invites, change a role, and remove a member.
 * The controls are hidden from everyone else (see canManageMembers) — the API
 * enforces the same rule regardless.
 * Route: /projects/:projectId/members
 */

import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AlertCircle, CheckCircle, Mail, Plus, RefreshCw, Send, Trash2, Users, X } from 'lucide-react';
import { useAuth } from '../lib/auth.js';
import { projectsApi, projectInvitesApi, type InviteRole, type ProjectInvite } from '../lib/api.js';
import {
  INVITE_ROLE_OPTIONS, canManageMembers, describeInviteOutcome, isOnlyProjectAdmin, projectRoleLabel,
} from '../lib/projectMembers.js';

interface Member {
  id:         string;
  email:      string;
  full_name?: string | null;
  role:       string;
  added_at:   string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

// ---------------------------------------------------------------------------
// Invite form
// ---------------------------------------------------------------------------

function InviteForm({
  onCancel,
  onInvite,
}: {
  onCancel: () => void;
  onInvite: (email: string, role: InviteRole) => Promise<void>;
}) {
  const [email, setEmail]     = useState('');
  const [role, setRole]       = useState<InviteRole>('consultant');
  const [loading, setLoading] = useState(false);
  const [err, setErr]         = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!EMAIL_RE.test(trimmed)) { setErr('Enter a valid email address.'); return; }
    setLoading(true);
    setErr('');
    try {
      await onInvite(trimmed, role);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to send invite');
    } finally {
      setLoading(false);
    }
  };

  const hint = INVITE_ROLE_OPTIONS.find((o) => o.value === role)?.hint;

  return (
    <form onSubmit={handleSubmit} className="card p-4 space-y-3" aria-label="Invite to project">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-sm font-semibold text-ink">Invite to this project</h3>
        <button type="button" onClick={onCancel} className="btn-icon text-muted hover:text-ink" aria-label="Close invite form">
          <X size={16} />
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_200px] gap-2">
        <div>
          <label className="label mb-1" htmlFor="invite-email">Email address</label>
          <input
            id="invite-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@company.com"
            className="input text-sm"
            autoFocus
          />
        </div>
        <div>
          <label className="label mb-1" htmlFor="invite-role">Role</label>
          <select id="invite-role" value={role} onChange={(e) => setRole(e.target.value as InviteRole)} className="select text-sm">
            {INVITE_ROLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>
      {hint && <p className="text-[11px] font-mono text-muted">{hint}</p>}
      {err && (
        <div className="flex items-center gap-2 text-sm text-danger" role="alert">
          <AlertCircle size={13} /> {err}
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="btn-ghost text-sm">Cancel</button>
        <button type="submit" disabled={loading} className="btn-primary text-sm">
          <Mail size={14} /> {loading ? 'Sending…' : 'Send invite'}
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// A button that asks "sure?" inline instead of using a blocking browser dialog
// ---------------------------------------------------------------------------

function ConfirmButton({
  label, confirmLabel, onConfirm, title,
}: {
  label: React.ReactNode;
  confirmLabel: string;
  onConfirm: () => void;
  title: string;
}) {
  const [asking, setAsking] = useState(false);
  if (!asking) {
    return (
      <button onClick={() => setAsking(true)} className="btn-icon w-7 h-7 text-muted hover:text-danger" title={title} aria-label={title}>
        {label}
      </button>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-mono">
      <button onClick={() => { setAsking(false); onConfirm(); }} className="text-danger font-semibold hover:underline">{confirmLabel}</button>
      <button onClick={() => setAsking(false)} className="text-muted hover:text-ink">Keep</button>
    </span>
  );
}

// ---------------------------------------------------------------------------
// ProjectMembersPage
// ---------------------------------------------------------------------------

export function ProjectMembersPage() {
  const { projectId = '' } = useParams();
  const { user, devHeaders } = useAuth();

  const [members, setMembers]     = useState<Member[]>([]);
  const [invites, setInvites]     = useState<ProjectInvite[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [notice, setNotice]       = useState('');
  const [showInvite, setShowInvite] = useState(false);

  const canManage = canManageMembers(members, { userId: user.userId, tenantRole: user.tenantRole });

  const load = useCallback(async () => {
    if (!projectId) return;
    try {
      const list = (await projectsApi.members(projectId, devHeaders)) as Member[];
      setMembers(list);
      setError('');
      // The invite list is managers-only: a non-manager's 403 is expected, not an error to show.
      try {
        setInvites(await projectInvitesApi.list(projectId, devHeaders));
      } catch {
        setInvites([]);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load project members');
    } finally {
      setLoading(false);
    }
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setLoading(true); void load(); }, [load]);

  const flash = (message: string) => {
    setNotice(message);
    setTimeout(() => setNotice(''), 6000);
  };

  const run = async (action: () => Promise<void>, fallback: string) => {
    setError('');
    try {
      await action();
    } catch (e) {
      setError(e instanceof Error ? e.message : fallback);
    }
  };

  const handleInvite = async (email: string, role: InviteRole) => {
    const result = await projectInvitesApi.invite(projectId, { email, role }, devHeaders);
    setShowInvite(false);
    flash(describeInviteOutcome(result, email));
    await load();
  };

  const handleResend = (invite: ProjectInvite) =>
    run(async () => {
      const result = await projectInvitesApi.resend(projectId, invite.id, devHeaders);
      flash(result.emailSent ? `Invite re-sent to ${invite.email}.` : `Invite extended for ${invite.email}, but no email was sent.`);
      await load();
    }, 'Failed to resend the invite');

  const handleRevoke = (invite: ProjectInvite) =>
    run(async () => {
      await projectInvitesApi.revoke(projectId, invite.id, devHeaders);
      flash(`Invite to ${invite.email} revoked.`);
      await load();
    }, 'Failed to revoke the invite');

  const handleRoleChange = (member: Member, role: InviteRole) =>
    run(async () => {
      await projectInvitesApi.changeRole(projectId, member.id, role, devHeaders);
      flash(`${member.full_name ?? member.email} is now ${projectRoleLabel(role).toLowerCase()}.`);
      await load();
    }, 'Failed to change the role');

  const handleRemove = (member: Member) =>
    run(async () => {
      await projectInvitesApi.removeMember(projectId, member.id, devHeaders);
      flash(`${member.full_name ?? member.email} removed from the project.`);
      await load();
    }, 'Failed to remove the member');

  return (
    <div className="p-8 max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="page-title">Project members</h1>
          {!loading && (
            <p className="text-sm text-muted mt-1">
              {members.length} member{members.length !== 1 ? 's' : ''}
              {canManage && invites.length > 0 && ` · ${invites.length} pending invite${invites.length !== 1 ? 's' : ''}`}
            </p>
          )}
        </div>
        {canManage && !showInvite && (
          <button onClick={() => setShowInvite(true)} className="btn-primary text-sm">
            <Plus size={14} /> Invite
          </button>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-danger/8 rounded-lg text-danger text-sm mb-4" role="alert">
          <AlertCircle size={14} className="shrink-0" /> {error}
        </div>
      )}
      {notice && (
        <div className="flex items-center gap-2 p-3 bg-success/8 rounded-lg text-success text-sm mb-4" role="status">
          <CheckCircle size={14} className="shrink-0" /> {notice}
        </div>
      )}

      {canManage && showInvite && (
        <div className="mb-4"><InviteForm onCancel={() => setShowInvite(false)} onInvite={handleInvite} /></div>
      )}

      {/* Members */}
      {loading ? (
        <div className="card animate-pulse space-y-px">{[1, 2, 3].map((i) => <div key={i} className="h-14 bg-gray/40" />)}</div>
      ) : members.length === 0 ? (
        <div className="empty-state py-12">
          <Users size={24} className="text-muted/40 mb-2 mx-auto" />
          <p className="text-sm text-muted">No one is on this project yet.</p>
        </div>
      ) : (
        <div className="card overflow-hidden mb-6">
          <table className="w-full text-sm" aria-label="Project members">
            <thead>
              <tr>
                <th className="table-header">Member</th>
                <th className="table-header w-48">Role</th>
                <th className="table-header w-32">Added</th>
                {canManage && <th className="table-header w-24" />}
              </tr>
            </thead>
            <tbody>
              {members.map((m) => {
                const onlyAdmin = isOnlyProjectAdmin(members, m.id);
                return (
                  <tr key={m.id} className="table-row">
                    <td className="table-cell">
                      <p className="text-sm font-medium text-ink">
                        {m.full_name ?? '—'}
                        {m.id === user.userId && <span className="ml-1.5 text-[10px] font-mono text-muted">(you)</span>}
                      </p>
                      <p className="text-[11px] font-mono text-muted">{m.email}</p>
                    </td>
                    <td className="table-cell">
                      {canManage && m.role !== 'viewer' ? (
                        <select
                          value={m.role}
                          onChange={(e) => void handleRoleChange(m, e.target.value as InviteRole)}
                          disabled={onlyAdmin}
                          title={onlyAdmin ? 'The only project admin — make someone else an admin first' : undefined}
                          className="select text-sm"
                          aria-label={`Role for ${m.email}`}
                        >
                          {INVITE_ROLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      ) : (
                        <span className="text-[11px] font-mono px-2 py-0.5 rounded-full bg-sky text-ink">{projectRoleLabel(m.role)}</span>
                      )}
                    </td>
                    <td className="table-cell text-[11px] font-mono text-muted">{formatDate(m.added_at)}</td>
                    {canManage && (
                      <td className="table-cell text-right">
                        {!onlyAdmin && (
                          <ConfirmButton
                            label={<Trash2 size={13} />}
                            confirmLabel="Remove"
                            title={`Remove ${m.email}`}
                            onConfirm={() => void handleRemove(m)}
                          />
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pending invites (managers only) */}
      {canManage && invites.length > 0 && (
        <>
          <h2 className="font-display text-sm font-semibold text-ink mb-2">Pending invites</h2>
          <div className="card overflow-hidden">
            <table className="w-full text-sm" aria-label="Pending invites">
              <thead>
                <tr>
                  <th className="table-header">Email</th>
                  <th className="table-header w-40">Role</th>
                  <th className="table-header w-44">Status</th>
                  <th className="table-header w-28" />
                </tr>
              </thead>
              <tbody>
                {invites.map((inv) => (
                  <tr key={inv.id} className="table-row">
                    <td className="table-cell">
                      <p className="text-sm text-ink">{inv.email}</p>
                      <p className="text-[11px] font-mono text-muted">
                        {inv.invited_by_name ? `Invited by ${inv.invited_by_name} · ` : ''}sent {formatDate(inv.last_sent_at)}
                      </p>
                    </td>
                    <td className="table-cell">
                      <span className="text-[11px] font-mono px-2 py-0.5 rounded-full bg-sky text-ink">{projectRoleLabel(inv.role)}</span>
                    </td>
                    <td className="table-cell text-[11px] font-mono text-muted">
                      {inv.status === 'expired' ? 'Expired' : `Pending · expires ${formatDate(inv.expires_at)}`}
                    </td>
                    <td className="table-cell text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => void handleResend(inv)}
                          className="btn-icon w-7 h-7 text-muted hover:text-ink"
                          title={`Resend invite to ${inv.email}`}
                          aria-label={`Resend invite to ${inv.email}`}
                        >
                          <Send size={13} />
                        </button>
                        <ConfirmButton
                          label={<X size={13} />}
                          confirmLabel="Revoke"
                          title={`Revoke invite to ${inv.email}`}
                          onConfirm={() => void handleRevoke(inv)}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!canManage && !loading && (
        <div className="flex items-start gap-2 mt-4 p-3 bg-sky rounded-lg">
          <RefreshCw size={13} className="text-blue shrink-0 mt-0.5" />
          <p className="text-[11px] font-mono text-blue/80">
            Only a project admin can invite people or change roles. Ask one of the admins above.
          </p>
        </div>
      )}
    </div>
  );
}
